<template>
  <div>
    <data-table
      :key="tableKey"
      :tableOpts="tableOpts"
      :loadData="loadData"
      :apiMode="true"
      @cell-clicked="onCellClicked"
    />

    <list-item-form
      v-if="showModal"
      :item="itemToEdit"
      :config="modalConfig"
      @close="closeModal"
      @refresh-table="refreshTable"
    />

    <excel-import
      v-if="showExcelModal"
      :isExcelModalActive="showExcelModal"
      :batchSize="100"
      :importLimit="MAX_TAGS - totalTags"
      colsToUse="tagCols"
      importModule="tag-import"
      :uniqueFields="['name']"
      :validateExcelItem="validateExcelTagItem"
      :saveExcelItem="saveExcelTagItem"
      @close="closeExcelModal"
      @close-modal="closeExcelModal"
      @refresh-table="refreshTable"
    />
  </div>
</template>

<script>
import { reactive, toRefs, defineComponent, h } from 'vue';
import { cloneDeep, get } from 'lodash';
import { useToast } from 'vue-toastification';
import { DialogProgrammatic } from '@/components/Dialog';
import { ModalProgrammatic } from '@oruga-ui/oruga-next';
import DataTable from '@/components/table-fields/DataTable.vue';
import ListItemForm from '@/components/modals/ListItemForm.vue';
import Tags from '@/models/Companies';
import Validator from '@/components/utils/Validations';
import TagColorPicker from '../TagColorPicker.vue';
import ExcelImport from '@/components/ExcelImport.vue';

export default {
  name: 'Tags',

  components: {
    'data-table': DataTable,
    'list-item-form': ListItemForm,
    'mf-tag-color-cell': TagColorPicker,
    'excel-import': ExcelImport,
  },

  props: {
    projId: {
      type: String,
      default: null,
    },
    searchText: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const toast = useToast();

    const MAX_TAGS = 1024;
    const data = reactive({
      tableKey: 0,
      showModal: false,
      itemToEdit: {},
      totalTags: 0,
      maxTags: 1024,
      showExcelModal: false,
    });

    const fetchUniqueColor = (existingColors = []) => {
      const randomColor = `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`;
      if (
        existingColors.includes(randomColor)
        || !/^#[0-9A-F]{6}$/i.test(randomColor)
      ) {
        return fetchUniqueColor(existingColors);
      }
      return randomColor;
    };

    const modalConfig = {
      label: 'Tag',
      showSubCategories: false,
      showColor: true,
      saveFn: async (payload) => {
        const result = await Tags.addTags({
          ...payload,
          scope: props.projId ? 'project' : 'company',
          ...(props.projId && { project: props.projId }),
        });
        const { valids, duplicates, limitReached } = result;
        if (limitReached) {
          return {
            failedData: [{
              _errorMsg: `Cannot add more tags — company tag limit of ${MAX_TAGS} reached!`,
            }],
          };
        }
        if (duplicates?.length) {
          return {
            failedData: [{
              _errorMsg: `A tag named "${duplicates[0]}" (or with that color) already exists!`,
            }],
          };
        }
        return { data: valids };
      },
      updateFn: async (payload) => {
        const result = await Tags.updateTag(payload);
        if (result?.data?.duplicates?.length) {
          throw new Error(
            `The name or color provided for "${payload.name}" is already in use!`,
          );
        }
      },
    };

    const fields = [
      {
        title: 'Name',
        id: 'name',
        prop: 'name',
        name: 'field-generic-input',
        width: '400px',
        visible: true,
        alwaysVisible: true,
        inputProps: {
          type: 'text',
          openCard: false,
        },
      },
    ];
    if (!props.projId) {
      fields.push({
        title: 'Color',
        id: 'color',
        prop: 'color',
        name: 'mf-tag-color-cell',
        formatter: (val) => (/^#[0-9A-F]{6}$/i.test(val) ? val : '#cccccc'),
        width: '48px',
        dataClass: 'color-cell',
        visible: true,
        defaultVisible: true,
        alwaysVisible: true,
      });
    }

    fields.push({
      title: 'ACTIONS',
      name: 'table-field-actions',
      id: 'action',
      hideFromChooser: true,
      alwaysVisible: true,
      defaultVisible: true,
      width: '112px',
      permissionModule: 'app&projects',
      permissionKey: props.projId ? 'projects' : 'company_lists',
      inputProps: {
        showEdit: false,
        groupButtons: (rowData) => {
          if (rowData.isDefault) return [];
          return [
            {
              icon: 'icon-edit',
              event: 'edit',
              tooltip: 'Edit Tag',
              permissionModule: 'app&projects',
              alterDataKey: 'lists',
            },
            {
              icon: 'icon-bin',
              event: 'delete',
              tooltip: 'Delete Tag',
              permissionModule: 'app&projects',
              alterDataKey: 'lists',
            },
          ];
        },
      },
    });

    const tableOpts = {
      defaultSortField: 'name',
      defaultSortOrder: 'asc',
      stickeyHeaders: false,
      fields,
    };

    const loadData = async (params) => {
      const query = {
        limit: params.limit,
        page: params.page,
        search: props.searchText || '',
        sort: params.sort,
        ...(props.projId && { project: props.projId }),
      };

      const result = await Tags.getTags(query);
      if (!result?.data) result.data = [];
      data.totalTags = result.total || 0;
      return result;
    };

    const refreshTable = () => {
      data.tableKey += 1;
    };

    const openModal = (item = {}) => {
      if (!item._id) {
        item = { color: props.projId ? '#43AAC7' : fetchUniqueColor(), ...item };
      }
      data.itemToEdit = cloneDeep(item);
      data.showModal = true;
    };

    const closeModal = () => {
      data.showModal = false;
      data.itemToEdit = {};
    };

    const importExcel = () => {
      if (data.totalTags >= MAX_TAGS) {
        toast.error(`Cannot add more tags — company tag limit of ${MAX_TAGS} reached!`);
        return;
      }
      data.showExcelModal = true;
    };

    const closeExcelModal = () => {
      data.showExcelModal = false;
    };

    const validateExcelTagItem = (obj) => {
      obj.color = fetchUniqueColor();
      obj.scope = 'company';
      return Validator.validateTag({
        tagName: obj.name,
        showError: false,
        skipDuplicateCheck: true,
      });
    };

    const saveExcelTagItem = async (obj, _fileName, isFinalBatch) => {
      let result = {};
      try {
        if (obj && Object.keys(obj).length) {
          result = await Tags.addTags(obj);
        }
      } catch {
        toast.error('Error saving tags!');
      }
      if (isFinalBatch) refreshTable();
      return result;
    };
    const onCellClicked = ({ type, data: rowData }) => {
      if (type === 'edit') {
        openModal(rowData);
      } else if (type === 'delete') {
        DialogProgrammatic.confirm({
          title: 'Delete Tag',
          message: `Are you sure you want to delete "${rowData.name}"? This will remove the tag from all orders where it is used.`,
          okButton: 'Delete',
          type: 'danger',
          onConfirm: async () => {
            try {
              await Tags.archiveTag(rowData._id, get(rowData, 'project._id'));
              toast.success(`Tag "${rowData.name}" deleted successfully`);
              refreshTable();
            } catch (e) {
              toast.error(
                get(e, 'data.message') || e.message || 'Error deleting tag',
              );
            }
          },
        });
      }
    };

    return {
      ...toRefs(data),
      modalConfig,
      tableOpts,
      loadData,
      onCellClicked,
      refreshTable,
      openModal,
      closeModal,
      importExcel,
      closeExcelModal,
      validateExcelTagItem,
      saveExcelTagItem,

    };
  },
};
</script>

<style scoped>
div :deep(.color-cell) {
  padding-right: 10px !important;
  vertical-align: middle;
}
div :deep(.color-cell .mfact-active-color) {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: block;
}
</style>